<template>
  <div
    v-bind="attrs"
    class="buttons top-bar bar-wrap"
    :data-visibility="props.visibilityManagedByShell ? 'fixed' : componentVisibility"
    :data-bar-style="toolbarStore.topBarStyle"
    :class="{
      'bar-hidden': !props.visibilityManagedByShell && autoHideHidden,
      'stacked-bar': props.stacked === true,
    }"
    v-if="toolbarStore.topItems.length > 0"
    @mouseenter="revealBar"
    @mouseleave="startHideTimer"
    @touchstart.passive="revealBar"
  >
    <template v-for="item in toolbarStore.topItems" :key="item.key">
      <!-- prev-next -->
      <div v-if="item.id === 'prev-next'" class="button-section prev-next">
        <button @click="emit('prev')" :disabled="current <= 0" aria-label="Previous" title="Previous">
          <ChevronLeft />
        </button>
        <InputPopover
          :modelValue="current"
          @update:modelValue="(value) => emit('goto', value)"
          type="number"
          min="0"
        />
        <button
          @click="emit('next')"
          :disabled="current >= apiConfig.fileUrls.length - 1"
          aria-label="Next"
          title="Next"
        >
          <ChevronRight />
        </button>
      </div>

      <!-- page -->
      <div v-else-if="item.id === 'page'" class="button-section page-section">
        <PageSelectorDropdown />
      </div>

      <!-- search -->
      <div v-else-if="item.id === 'search'" class="button-section search-section">
        <SearchBar />
      </div>

      <!-- refresh -->
      <div v-else-if="item.id === 'refresh'" class="button-section buttonish-section">
        <button @click="emit('reset')" title="Refresh hydrus page" aria-label="Refresh">
          <RefreshCw /><span class="btn-label">Refresh</span>
        </button>
      </div>

      <!-- tag-overlay -->
      <div v-else-if="item.id === 'tag-overlay'" class="button-section buttonish-section">
        <button @click="emit('openTagOverlay')" title="Open tag menu" aria-label="Open tag menu">
          <Tag /><span class="btn-label">Tags</span>
        </button>
      </div>

      <!-- vote-overlay -->
      <div v-else-if="item.id === 'vote-overlay'" class="button-section buttonish-section">
        <button @click="emit('openVoteOverlay')" title="Open vote menu" aria-label="Open vote menu">
          <Star /><span class="btn-label">Vote</span>
        </button>
      </div>

      <!-- meta-overlay -->
      <div v-else-if="item.id === 'meta-overlay'" class="button-section buttonish-section" title="Show/hide meta overlays (h)">
        <button @click="emit('showMetaOverlay', true)" v-if="!showUrlsAndVotes" aria-label="Show meta overlay">
          <Eye /><span class="btn-label">Meta</span>
        </button>
        <button @click="emit('showMetaOverlay', false)" v-else aria-label="Hide meta overlay">
          <EyeOff /><span class="btn-label">Meta</span>
        </button>
      </div>

      <!-- autoplay -->
      <div v-else-if="item.id === 'autoplay'" class="button-section" title="Autoplay">
        <button @click="emit('autoplay', true)" v-if="!autoplay" aria-label="Start autoplay">
          <Play />
        </button>
        <button @click="emit('autoplay', false)" v-if="autoplay" aria-label="Stop autoplay">
          <Pause />
        </button>
        <input type="number" v-model="intervalValue" style="width: 2em" />
      </div>

      <!-- image-size -->
      <div v-else-if="item.id === 'image-size'" class="button-section" title="Image size restrain mode">
        <span @click="emit('nextImageSizeRestrainMode')" style="cursor:pointer;" aria-label="Image size mode">
          <Scaling /><span>{{ props.imageSizeRestrainMode }}</span>
        </span>
      </div>

      <!-- zoom -->
      <div v-else-if="item.id === 'zoom'" class="button-section buttonish-section" title="Enable/disable zoom overlay">
        <button @click="apiConfig.zoomEnabled = true" v-if="!apiConfig.zoomEnabled" aria-label="Enable zoom">
          <ZoomIn /><span class="btn-label">Zoom</span>
        </button>
        <button @click="apiConfig.zoomEnabled = false" v-else aria-label="Disable zoom">
          <ZoomOut /><span class="btn-label">Zoom</span>
        </button>
      </div>

      <!-- fullscreen -->
      <div v-else-if="item.id === 'fullscreen'" class="button-section buttonish-section">
        <button @click="emit('fullscreen')" title="Toggle fullscreen" aria-label="Toggle fullscreen">
          <Maximize /><span class="btn-label">Fullscreen</span>
        </button>
      </div>

      <!-- page-reload -->
      <div v-else-if="item.id === 'page-reload'" class="button-section buttonish-section">
        <button @click="webReload()" title="Reload this webpage" aria-label="Reload page">
          <RotateCcw /><span class="btn-label">Reload</span>
        </button>
      </div>

      <!-- preload-stats -->
      <div v-else-if="item.id === 'preload-stats'" class="button-section preload-stats" title="Preloading stats">
        <span>
          <ImageIcon :size="12" /> Img:
          {{
            Object.values(props.preloadedImages).filter(
              (item: ReactiveImage | null): boolean => {
                return item !== null && item.loaded.value === true;
              }
            ).length
          }}
        </span>
        <span>Meta: {{ Object.values(props.preloadedMeta).filter((item) => item !== null).length }}</span>
      </div>

      <!-- fetch-stats -->
      <div v-else-if="item.id === 'fetch-stats'" class="button-section fetch-stats" title="Pending requests" @click="emit('toggleRequestStats')">
        <span>
          <span><Clock :size="12" /> {{ requestTracker.requests.length }}</span>
          <span><ImageIcon :size="12" /> {{ requestTracker.requests.filter((req) => req.type === 'img').length }}</span>
        </span>
        <span>
          <span><Video :size="12" /> {{ requestTracker.requests.filter((req) => req.type === 'video').length }}</span>
          <span><Bot :size="12" /> {{ requestTracker.requests.filter((req) => req.type === 'fetch').length }}</span>
        </span>
      </div>

      <!-- viewer-mode -->
      <div v-else-if="item.id === 'viewer-mode'" class="button-section" title="Select GUI mode">
        <select v-model="apiConfig.viewerMode">
          <option
            v-for="(value, key) in { single: 'single', mobile: 'mobile', desktop: 'desktop' }"
            :key="key"
            :value="key"
            :title="key"
          >
            {{ key }}
          </option>
        </select>
      </div>

      <!-- mouse-votes -->
      <div v-else-if="item.id === 'mouse-votes'" class="button-section buttonish-section" title="Enable/disable double click to vote">
        <button @click="apiConfig.mouseVotes = true" v-if="!apiConfig.mouseVotes" aria-label="Enable mouse votes">
          <MousePointerClick /><span class="btn-label">Mouse votes</span>
        </button>
        <button @click="apiConfig.mouseVotes = false" v-else aria-label="Disable mouse votes">
          <Mouse /><span class="btn-label">Mouse votes</span>
        </button>
      </div>

      <!-- settings -->
      <div v-else-if="item.id === 'settings'" class="button-section buttonish-section">
        <button @click="showSettings = true" title="Settings (S / Ctrl+,)" aria-label="Settings">
          <Settings /><span class="btn-label">Settings</span>
        </button>
      </div>

      <!-- spacer -->
      <BarSpacer v-else-if="item.id === 'spacer'" />
    </template>

  </div>
  <Teleport to="body">
    <SettingsPanel
      v-if="showSettings"
      @close="showSettings = false"
      @prev="emit('prev')"
      @next="emit('next')"
      @reset="emit('reset')"
      @open-tag-overlay="emit('openTagOverlay')"
      @open-vote-overlay="emit('openVoteOverlay')"
      :current="current"
      :total="apiConfig.fileUrls.length"
    />
  </Teleport>
</template>

<script setup lang="ts">
import type { ReactiveImage } from "@/libraries/vue-ractive-new-html-image/ref-img";
import type { Metadata } from "@/stores/ImageMetadata";
import { useApiStore } from "@/stores/api";
import { computed, onMounted, onUnmounted, ref, useAttrs, watch } from "vue";
import { useRequestTrackerStore } from "@/libraries/vue-fetch/stores/requestTracker";
import { type BarVisibility, useToolbarStore } from "@/stores/toolbar";
import InputPopover from "@/components/InputPopover.vue";
import SettingsPanel from "@/components/SettingsPanel.vue";
import SearchBar from "@/components/search/SearchBar.vue";
import PageSelectorDropdown from "@/components/PageSelectorDropdown.vue";
import BarSpacer from "@/components/BarSpacer.vue";
import {
  Bot,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  EyeOff,
  Image,
  Maximize,
  Mouse,
  MousePointerClick,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Scaling,
  Settings,
  Star,
  Tag,
  Video,
  ZoomIn,
  ZoomOut,
} from "lucide-vue-next";
const ImageIcon = Image;

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  current: number;
  preloadedImages: { [key: string]: ReactiveImage | null };
  preloadedMeta: { [key: number]: Metadata };
  showUrlsAndVotes: boolean;
  imageSizeRestrainMode: number;
  autoplay: boolean;
  isVisible?: boolean;
  stacked?: boolean;
  visibilityManagedByShell?: boolean;
  visibilityOverride?: BarVisibility;
}>();

const emit = defineEmits<{
  (e: "prev"): void;
  (e: "next"): void;
  (e: "goto", to: number): void;
  (e: "zero"): void;
  (e: "reset"): void;
  (e: "openVoteOverlay"): void;
  (e: "openTagOverlay"): void;
  (e: "showMetaOverlay", state: boolean): void;
  (e: "fullscreen"): void;
  (e: "nextImageSizeRestrainMode"): void;
  (e: "autoplay", state: boolean): void;
  (e: "toggleRequestStats"): void;
}>();
const attrs = useAttrs();

const apiConfig = useApiStore();
const requestTracker = useRequestTrackerStore();
const toolbarStore = useToolbarStore();
const componentVisibility = computed<BarVisibility>(() =>
  props.visibilityOverride ?? toolbarStore.topBarVisibility
);
const showSettings = ref<boolean>(false);

async function webReload() {
  if (typeof window !== "undefined") {
    window.location.reload();
  }
}

const intervalValue = ref<number>(1.0);
watch(
  () => [props.autoplay, intervalValue.value],
  (newValues) => {
    const autoplay = newValues[0] as typeof props.autoplay;
    const seconds = newValues[1] as typeof intervalValue.value;
    if (autoplayTimer.value !== null) {
      window.clearInterval(autoplayTimer.value);
      autoplayTimer.value = null;
    }
    if (autoplay) {
      autoplayTimer.value = window.setInterval(autoNext, seconds * 1000);
    }
  }
);
const autoplayTimer = ref<null | number>(null);

function autoNext() {
  if (props.current === apiConfig.fileUrls.length - 1) {
    emit("zero");
    return;
  }
  emit("next");
}

function isTyping(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable;
}

function onGlobalKeyDown(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === ",") {
    showSettings.value = !showSettings.value;
    e.preventDefault();
    e.stopImmediatePropagation();
  } else if (e.key === "s" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e)) {
    showSettings.value = !showSettings.value;
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}
onMounted(() => document.addEventListener("keydown", onGlobalKeyDown));
onUnmounted(() => {
  document.removeEventListener("keydown", onGlobalKeyDown);
  clearHideTimer();
});

const autoHideHidden = ref(false);
let hideTimer: number | null = null;

function clearHideTimer() {
  if (hideTimer !== null) { window.clearTimeout(hideTimer); hideTimer = null; }
}

function revealBar() {
  if (props.visibilityManagedByShell) return;
  autoHideHidden.value = false;
  clearHideTimer();
  if (componentVisibility.value === "autohide") startHideTimer();
}

function startHideTimer() {
  if (props.visibilityManagedByShell) return;
  if (componentVisibility.value !== "autohide") return;
  clearHideTimer();
  hideTimer = window.setTimeout(() => { autoHideHidden.value = true; }, 20000);
}

watch(componentVisibility, (v) => {
  if (props.visibilityManagedByShell) {
    clearHideTimer();
    autoHideHidden.value = false;
    return;
  }
  if (v === "autohide") startHideTimer();
  else { clearHideTimer(); autoHideHidden.value = false; }
}, { immediate: true });
</script>

<style scoped lang="scss">
/* Bar style: icon-only (default) hides labels; label-only hides icons; icon+label shows both */
.buttons[data-bar-style="icon-only"] .btn-label { display: none; }
.buttons[data-bar-style="label-only"] svg { display: none; }
.btn-label { font-size: 0.8em; white-space: nowrap; }

.bar-wrap {
  transition: opacity var(--transition-base, 0.2s ease);

  &[data-visibility="autohide"].bar-hidden {
    opacity: 0;
    pointer-events: none;
  }

  &[data-visibility="hover"] {
    opacity: 0;
    pointer-events: none;
    &:hover { opacity: 1; pointer-events: auto; }
  }
}

.buttons {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  overflow-x: auto;
  overflow-y: visible;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border);
}

.buttons.stacked-bar {
  flex-direction: column;
  align-items: stretch;
  width: min(24rem, 92vw);
  max-width: 100%;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: 0 18px 36px color-mix(in srgb, var(--bg-base) 82%, transparent);
}

.buttons .button-section {
  flex: 0 0 auto;
  min-width: 0;
  height: 100%;
  background: transparent;
  border-right: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.875rem;
  line-height: 1.1;

  svg {
    width: 0.95em;
    height: 0.95em;
    flex: 0 0 auto;
  }

  &.buttonish-section:hover { background: var(--btn-hover-bg); }

  &.buttonish-section > button:hover {
    color: var(--accent);
  }

  & > button {
    height: 100%;
    background: transparent;
    color: var(--text-primary);
    border: none;
    cursor: pointer;
    transition: color var(--transition-fast);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0 0.5em;
    font-size: inherit;
    line-height: inherit;

    &:disabled { opacity: 0.35; cursor: default; }
  }

  select, input[type="text"], input[type="number"] {
    background: var(--bg-elevated);
    color: var(--text-primary);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }

  & > span {
    padding-left: 1em;
    padding-right: 1em;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: inherit;
  }

  &.preload-stats, &.fetch-stats {
    font-size: 0.72rem;
    font-family: Monofonto, monospace;
    display: inline-flex;
    flex-direction: column;
    flex-wrap: wrap-reverse;
    align-items: flex-start;
    gap: 0.2rem;

    & > span {
      padding: 0;
      display: block;
      &:first-child, &:nth-child(2) { display: inline; align-self: initial; }
    }
  }
  &.fetch-stats { display: flex; flex-direction: row; flex-wrap: nowrap; justify-content: space-between; align-items: center; }
  &.prev-next { & > button, & > span { display: inline; } }
}

.buttons.stacked-bar .button-section {
  width: 100%;
  min-height: 3rem;
  border-right: none;
  border-bottom: 1px solid var(--border);
  padding: 0 0.75rem;
  box-sizing: border-box;

  &:last-child {
    border-bottom: none;
  }

  & > button {
    height: auto;
    min-height: 3rem;
    justify-content: flex-start;
  }
}
</style>
